import { Button, Flex, Input, Space, Typography } from 'antd'
import { Mail, Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { isInviteEmail, type OnboardingState } from '../useOnboardingState'

import { StepHeader } from './StepHeader'

const { Text } = Typography

type Props = { state: OnboardingState }

/**
 * Invite rows.
 *
 * No role picker. The invite endpoint stopped accepting a role —
 * `Assign workspace roles through the membership roles endpoint` — and roles are
 * now numeric `OrganizationRole` ids assigned in a second call. Offering the
 * choice here while nothing carried it would promise an outcome that does not
 * happen; roles are set in the workspace's members section instead.
 *
 * "Copy invite link" lives on the Done screen, not here: the workspace is
 * created after this step, so before that there is no URL to copy.
 */
export const InviteStep = ({ state }: Props) => {
  const { t } = useTranslation()
  const { draft, addInvite, removeInvite, updateInvite, focusProps } = state

  /**
   * Only marks a row once the step has actually been submitted — reddening an
   * address the moment it is half-typed is validation before touch.
   */
  const invalidEmail = (email: string) =>
    state.error?.field === 'invites' &&
    email.trim().length > 0 &&
    !isInviteEmail(email)

  const onlyRow = draft.invites.length === 1

  return (
    <Flex vertical gap={16}>
      <StepHeader title={t('workspaces.onboarding.invite.title')} />

      <Flex vertical gap={12}>
        {draft.invites.map((invite) => (
          <Flex key={invite.id} align="center" gap={8}>
            <Space.Compact style={{ flex: 1, minWidth: 0 }}>
              <Space.Addon>
                <Mail size={14} />
              </Space.Addon>
              <Input
                value={invite.email}
                type="email"
                // Three `type=email` boxes read to Chrome as one address plus
                // two "confirm address" repeats, so picking a suggestion in any
                // row planted it in all three. Distinct names alone did not
                // separate them, and `autocomplete="off"` is documented as
                // ignored for address fields — what Chrome does honour is a
                // token it cannot parse: an unrecognised value takes the field
                // out of address autofill altogether. Unique per row so the
                // rows cannot be grouped either.
                //
                // No loss here: the suggestion on offer is the account holder's
                // own address, which is not a teammate to invite.
                name={`invite-email-${invite.id}`}
                autoComplete={`invite-row-${invite.id}`}
                status={invalidEmail(invite.email) ? 'error' : undefined}
                placeholder={t('workspaces.onboarding.invite.emailPlaceholder')}
                onChange={(event) =>
                  updateInvite(invite.id, { email: event.target.value })
                }
                {...focusProps('members')}
              />
            </Space.Compact>

            {/* Removes on the click. Nothing is committed on this step — the
                row is a line in a draft the user is still writing, and asking
                twice about one of four empty boxes made deleting a typo feel
                like a decision. The last row cannot be removed at all. */}
            <Button
              danger
              type="text"
              icon={<Trash2 size={14} />}
              aria-label={t('workspaces.onboarding.invite.removeRow')}
              disabled={onlyRow}
              onClick={() => removeInvite(invite.id)}
            />
          </Flex>
        ))}
      </Flex>

      {state.error?.field === 'invites' ? (
        <Text type="danger">{t(state.error.key)}</Text>
      ) : null}

      <Button
        size="small"
        icon={<Plus size={13} />}
        style={{ alignSelf: 'flex-start' }}
        onClick={addInvite}
      >
        {t('workspaces.onboarding.invite.addAnother')}
      </Button>
    </Flex>
  )
}
